import React, { ReactNode, useRef, useEffect } from 'react';
import { useBottomSheet, SheetState } from '../../hooks/useBottomSheet';

interface BottomSheetProps {
  children: (sheetState: SheetState) => ReactNode;
  isOpen: boolean;
  onClose?: () => void;
  onClear?: () => void;
  initialState?: SheetState;
}

export default function BottomSheet({ 
  children, 
  isOpen,
  onClear,
  initialState = 'half' 
}: BottomSheetProps) {
  const {
    sheetState,
    sheetHeight,
    isDragging,
    setHalf,
    expand,
    dragHandlers,
  } = useBottomSheet(initialState);

  const contentRef = useRef<HTMLDivElement>(null);

  // Prevent scroll propagation to the map underneath
  useEffect(() => {
    const contentEl = contentRef.current;
    if (!contentEl) return;

    const handleTouchStart = (e: TouchEvent) => {
      // Store the initial touch position
      const touch = e.touches[0];
      contentEl.dataset.touchStartY = touch.clientY.toString();
      contentEl.dataset.scrollTopStart = contentEl.scrollTop.toString();
    };

    const handleTouchMove = (e: TouchEvent) => {
      const touch = e.touches[0];
      const startY = parseFloat(contentEl.dataset.touchStartY || '0');
      const scrollTopStart = parseFloat(contentEl.dataset.scrollTopStart || '0');
      const deltaY = touch.clientY - startY;
      
      const scrollTop = contentEl.scrollTop;
      const scrollHeight = contentEl.scrollHeight;
      const clientHeight = contentEl.clientHeight;
      const isScrollable = scrollHeight > clientHeight;

      if (!isScrollable) {
        // If not scrollable, allow the event to bubble (for sheet dragging)
        return;
      }

      const isAtTop = scrollTop <= 0;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 1;

      // Prevent default to stop map scrolling when content is scrollable and not at boundaries
      if (!isAtTop && !isAtBottom) {
        e.stopPropagation();
      } else if (isAtTop && deltaY > 0) {
        // At top and trying to scroll down - prevent to keep scrolling content
        e.stopPropagation();
      } else if (isAtBottom && deltaY < 0) {
        // At bottom and trying to scroll up - prevent to keep scrolling content
        e.stopPropagation();
      }
    };

    contentEl.addEventListener('touchstart', handleTouchStart, { passive: true });
    contentEl.addEventListener('touchmove', handleTouchMove, { passive: false });

    return () => {
      contentEl.removeEventListener('touchstart', handleTouchStart);
      contentEl.removeEventListener('touchmove', handleTouchMove);
    };
  }, [sheetState]);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop - only show when expanded */}
      {sheetState === 'full' && (
        <div
          className="fixed inset-0 bg-black/30 z-40 md:hidden"
          onClick={() => setHalf()}
        />
      )}

      {/* Bottom Sheet */}
      <div
        className={`fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl shadow-2xl z-50 md:hidden flex flex-col ${
          isDragging ? '' : 'transition-all duration-200 ease-out'
        }`}
        style={{
          height: `${sheetHeight}vh`,
          maxHeight: '90vh',
        }}
      >
        {/* Drag handle and header area */}
        <div
          className="w-full py-4 flex flex-col items-center cursor-grab active:cursor-grabbing touch-none relative"
          {...dragHandlers}
          onClick={() => {
            if (sheetState === 'collapsed') {
              expand();
            }
          }}
        >
          <div className="w-12 h-1.5 bg-gray-300 rounded-full mb-2" />
          
          {/* Header - visible only when collapsed */}
          {sheetState === 'collapsed' && (
            <div className="text-sm font-semibold text-gray-900 mt-1">
              Fare Results
            </div>
          )}

          {/* Clear button - visible when half or full, positioned at top-right */}
          {sheetState !== 'collapsed' && onClear && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onClear();
              }}
              className="absolute top-2 right-4 text-sm text-gray-600 hover:text-gray-900 font-medium"
            >
              Clear
            </button>
          )}
        </div>

        {/* Scrollable content - hidden when collapsed */}
        {sheetState !== 'collapsed' && (
          <div 
            ref={contentRef}
            className="flex-1 overflow-y-auto pb-28 px-4 overscroll-y-contain"
          >
            {children(sheetState)}
          </div>
        )}
      </div>
    </>
  );
}

